package org.zq.astrock_ai.quant

import kotlin.math.abs
import kotlin.math.max
import kotlin.math.min
import kotlin.math.sqrt

/**
 * 建议卖出区间（研究参考）。
 * 多方分析后汇合：结构压力 + 量能节点 + 均线 + 波动目标 + 分时翻红线 + 资金/公告 + 回测。
 */
data class SellZonePlan(
    val low: Double,
    val high: Double,
    /** 0~1，多方越一致越高 */
    val confidence: Double,
    val method: String,
    val reasons: List<String>,
    val anchors: List<Pair<String, Double>> = emptyList(),
    /** 各方视角一行摘要，如「结构：12.3」「翻红线：11.8」 */
    val perspectives: List<String> = emptyList(),
)

private data class SellPerspective(
    val name: String,
    val low: Double,
    val high: Double,
    val weight: Double,
    val note: String = "",
)

/**
 * @param signal BUY/SELL/HOLD，影响区间解释与紧迫度
 * @param backtestExitAvg 最近回测卖出均价（可选）
 * @param market 主力资金 + 公告 + 分时翻红上下文（可选）
 */
fun buildSellZonePlan(
    bars: List<QuantBar>,
    price: Double = 0.0,
    signal: String = "HOLD",
    backtestExitAvg: Double? = null,
    market: ZoneMarketContext? = null,
): SellZonePlan {
    val px = when {
        price > 0 -> price
        else -> bars.lastOrNull()?.close ?: 0.0
    }
    if (px <= 0 || bars.size < 10) {
        return SellZonePlan(0.0, 0.0, 0.0, "数据不足", emptyList())
    }

    val recent20 = bars.takeLast(20)
    val recent60 = bars.takeLast(60)
    val recent120 = bars.takeLast(120)
    val r20 = recent20.maxOf { it.high }
    val atr = atr14(bars).coerceAtLeast(px * 0.008)
    val bollUpper = bollUpper20(bars)
    val atrTp1 = px + atr * 1.5
    val atrTp2 = px + atr * 2.5
    val ma20 = sma(bars, 20)
    val ma60 = sma(bars, 60)

    val swingPeaks = findSwingHighs(recent120, left = 2, right = 2)
        .filter { it >= px * 0.995 }
        .sortedDescending()
        .distinctBy { roundPrice(it) }
        .take(3)
    val volumeNode = highVolumeNode(recent60, px)
    val primarySwing = swingPeaks.firstOrNull() ?: recent60.dropLast(3).maxOfOrNull { it.high } ?: r20
    val rejectCount = countResistanceRejects(bars, primarySwing)

    val anchors = buildList {
        add("近20日高点" to r20)
        add("近端结构高点" to primarySwing)
        swingPeaks.drop(1).forEachIndexed { i, p -> add("历史摆动高${i + 2}" to p) }
        if (bollUpper > 0) add("布林上轨" to bollUpper)
        volumeNode?.let { add("高量出货节点" to it) }
        if (ma20 > px * 0.995) add("MA20压制" to ma20)
        if (ma60 > px * 0.995) add("MA60压制" to ma60)
        add("ATR1.5目标" to atrTp1)
        add("ATR2.5目标" to atrTp2)
        backtestExitAvg?.takeIf { it > px * 0.9 && it < px * 1.25 }?.let {
            add("回测离场均价" to it)
        }
        market?.preClose?.takeIf { it > px * 0.9 }?.let { add("昨收翻红线" to it) }
        market?.dayHigh?.takeIf { it >= px * 0.995 }?.let { add("分时日内高" to it) }
        market?.vwap?.takeIf { it >= px * 0.995 }?.let { add("分时均价" to it) }
    }

    // ─── 多方视角各自给带 ───
    val perspectives = mutableListOf<SellPerspective>()

    // 1) 结构压力
    val structLow = max(px * 1.003, min(primarySwing * 0.985, r20 * 0.99))
    val structHigh = max(structLow * 1.008, max(primarySwing * 1.01, r20))
    perspectives += SellPerspective(
        name = "结构",
        low = structLow,
        high = structHigh,
        weight = if (rejectCount >= 2) 1.35 else 1.15,
        note = "近端高点${roundPrice(primarySwing)}/20日高${roundPrice(r20)}" +
            if (rejectCount > 0) "·冲高回落${rejectCount}次" else "",
    )

    // 2) 量能出货节点
    volumeNode?.let { node ->
        perspectives += SellPerspective(
            name = "量能",
            low = max(px * 1.002, node * 0.99),
            high = max(node * 1.01, px * 1.01),
            weight = 1.05,
            note = "高量节点${roundPrice(node)}",
        )
    }

    // 3) 均线压制
    val maCaps = listOfNotNull(
        ma20.takeIf { it > px * 0.995 },
        ma60.takeIf { it > px * 0.995 },
    )
    if (maCaps.isNotEmpty()) {
        val maL = max(px * 1.002, maCaps.minOrNull()!! * 0.995)
        val maH = max(maL * 1.006, maCaps.maxOrNull()!! * 1.008)
        perspectives += SellPerspective(
            name = "均线",
            low = maL,
            high = maH,
            weight = 1.0,
            note = maCaps.joinToString("/") { roundPrice(it).toString() },
        )
    }

    // 4) 波动目标（ATR/布林）
    val volHigh = listOfNotNull(atrTp1, atrTp2.takeIf { it <= px * 1.12 }, bollUpper.takeIf { it > px })
        .filter { it >= px * 1.002 }
    if (volHigh.isNotEmpty()) {
        val vLow = max(px * 1.005, volHigh.minOrNull()!! * 0.98)
        val vHigh = max(vLow * 1.008, volHigh.maxOrNull()!!)
        perspectives += SellPerspective(
            name = "波动",
            low = vLow,
            high = vHigh,
            weight = 0.9,
            note = "ATR${roundPrice(atr)}·布林上${if (bollUpper > 0) roundPrice(bollUpper) else "--"}",
        )
    }

    // 5) 分时/翻红（隔夜反抽、早盘翻红关键）
    val reboundMode = market?.reboundSellMode == true
    val preClose = market?.preClose?.takeIf { it > 0 }
    if (preClose != null) {
        val flip = preClose
        val micro = preClose * 1.012
        val dayHigh = market.dayHigh?.takeIf { it > 0 }
        val vwap = market.vwap?.takeIf { it > 0 }
        val flipLow = max(px * 1.002, min(flip, max(px * 1.003, flip * 0.998)))
        val flipHigh = when {
            px >= flip * 0.999 -> listOfNotNull(dayHigh, vwap?.takeIf { it >= px }, micro, px * 1.015)
                .filter { it >= px * 1.002 }
                .minOrNull()
                ?.coerceAtLeast(flipLow * 1.005)
                ?: micro
            else -> max(flipLow * 1.008, min(micro, dayHigh ?: micro))
        }
        perspectives += SellPerspective(
            name = "翻红",
            low = flipLow,
            high = max(flipLow * 1.006, flipHigh),
            weight = if (reboundMode) 1.55 else 0.85,
            note = "昨收${roundPrice(flip)}～微红${roundPrice(micro)}" +
                (dayHigh?.let { "·日内高${roundPrice(it)}" } ?: ""),
        )
    } else {
        market?.dayHigh?.takeIf { it >= px * 1.002 }?.let { dh ->
            perspectives += SellPerspective(
                name = "分时",
                low = max(px * 1.002, dh * 0.995),
                high = max(dh, px * 1.01),
                weight = 0.8,
                note = "日内高${roundPrice(dh)}",
            )
        }
    }

    // 6) 回测离场
    backtestExitAvg?.takeIf { it > px * 0.995 && it < px * 1.2 }?.let { exit ->
        perspectives += SellPerspective(
            name = "回测",
            low = max(px * 1.002, exit * 0.99),
            high = max(exit * 1.01, px * 1.01),
            weight = 0.75,
            note = "近期离场均价${roundPrice(exit)}",
        )
    }

    if (perspectives.isEmpty()) {
        return SellZonePlan(0.0, 0.0, 0.0, "无上方压力锚点", emptyList())
    }

    // ─── 加权汇合 ───
    val midpoints = perspectives.map { (it.low + it.high) / 2.0 to it.weight }
    val wSum = midpoints.sumOf { it.second }.coerceAtLeast(0.01)
    val consensusMid = midpoints.sumOf { it.first * it.second } / wSum
    val nearPerspectives = perspectives.filter {
        abs((it.low + it.high) / 2.0 - consensusMid) / consensusMid <= 0.045
    }.ifEmpty { perspectives }

    // 近簇加权平均 low/high
    val nW = nearPerspectives.sumOf { it.weight }.coerceAtLeast(0.01)
    var bandLow = nearPerspectives.sumOf { max(px * 1.002, it.low) * it.weight } / nW
    var bandHigh = nearPerspectives.sumOf { max(it.high, it.low * 1.006) * it.weight } / nW
    bandLow = max(px * 1.002, bandLow)
    bandHigh = max(bandLow * 1.006, bandHigh)

    // 反抽玩法：汇合结果再与翻红带取「更近可兑现」侧，但不丢弃其他视角信息
    if (reboundMode) {
        val flipP = perspectives.firstOrNull { it.name == "翻红" }
        if (flipP != null) {
            bandLow = (bandLow * 0.35 + flipP.low * 0.65)
            bandHigh = min(bandHigh, max(bandLow * 1.008, flipP.high))
            bandLow = max(px * 1.002, bandLow)
            bandHigh = max(bandLow * 1.006, bandHigh)
        }
    }

    // 多次冲高回落：再向结构压力收窄
    if (rejectCount >= 2) {
        val tightLow = max(px * 1.003, primarySwing * 0.985)
        val tightHigh = max(tightLow * 1.006, primarySwing * 1.015)
        bandLow = bandLow * 0.55 + tightLow * 0.45
        bandHigh = min(bandHigh, tightHigh).coerceAtLeast(bandLow * 1.006)
    }

    val side = signal.uppercase()
    val tech = when (side) {
        "SELL" -> roundPrice(min(px, bandLow).coerceAtLeast(px * 0.998)) to
            roundPrice(max(px * 1.005, min(bandHigh, r20 * 1.01)))
        "BUY" -> roundPrice(bandLow) to roundPrice(max(bandHigh, atrTp1))
        else -> roundPrice(bandLow) to roundPrice(bandHigh)
    }
    val marketAdj = evaluateZoneMarketAdjust(market)
    var (low, high) = applySellMarketAdjust(tech.first, tech.second, px, marketAdj)
    if (low <= 0 || high <= 0 || high < low) {
        return SellZonePlan(0.0, 0.0, 0.0, "区间无效", emptyList())
    }
    low = roundPrice(low)
    high = roundPrice(max(low * 1.006, high))

    val agreeCount = perspectives.count {
        val mid = (it.low + it.high) / 2.0
        abs(mid - (low + high) / 2.0) / ((low + high) / 2.0) <= 0.03
    }
    val confidence = (
        0.28 +
            agreeCount * 0.08 +
            (perspectives.size * 0.04).coerceAtMost(0.24) +
            (if (rejectCount >= 2) 0.08 else 0.0) +
            (if (volumeNode != null) 0.05 else 0.0) +
            (if (backtestExitAvg != null) 0.06 else 0.0) +
            (if (reboundMode && preClose != null) 0.08 else 0.0) +
            marketAdj.confidenceDelta
        ).coerceIn(0.30, 0.94)

    val perspectiveLines = perspectives
        .sortedByDescending { it.weight }
        .map {
            "${it.name}:${roundPrice(it.low)}–${roundPrice(it.high)}" +
                if (it.note.isNotBlank()) "（${it.note}）" else ""
        }

    val method = "多方汇合（${perspectives.joinToString("+") { it.name }}）" +
        when {
            reboundMode && preClose != null -> "·偏翻红兑现"
            marketAdj.riskScore >= 0.35 -> "·公告折价"
            marketAdj.flowScore >= 0.45 -> "·资金抬升"
            marketAdj.flowScore <= -0.45 -> "·资金走弱"
            agreeCount >= 3 -> "·高度一致"
            else -> ""
        }

    val reasons = buildList {
        add("多方汇合卖区 ${low}–${high}（置信 ${(confidence * 100).toInt()}%，${agreeCount}/${perspectives.size} 方贴近）")
        addAll(perspectiveLines.map { "视角·$it" })
        if (tech.first != low || tech.second != high) {
            add("技术汇合原区间 ${roundPrice(tech.first)}–${roundPrice(tech.second)}，已按资金/公告微调")
        }
        addAll(marketAdj.reasons)
        when {
            reboundMode -> add("玩法侧重：反抽/早盘翻红，翻红视角加权更高，但仍交叉验证结构/量能/波动")
            side == "BUY" -> add("偏多：上方压力与波动目标交叉，避免止盈过近")
            side == "SELL" -> add("偏空：优先近端多方压力带分批兑现")
            else -> add("中性：多方压力带触及可分批减仓")
        }
        add("说明：多视角汇合的研究兑现带，非必达价；不构成收益保证")
    }

    return SellZonePlan(
        low = low,
        high = high,
        confidence = (confidence * 100).toInt() / 100.0,
        method = method,
        reasons = reasons,
        anchors = anchors.map { it.first to roundPrice(it.second) },
        perspectives = perspectiveLines,
    )
}

/** 兼容旧调用：仅返回区间。 */
fun suggestSellZone(
    bars: List<QuantBar>,
    price: Double = 0.0,
): Pair<Double, Double> {
    val plan = buildSellZonePlan(bars, price)
    return plan.low to plan.high
}

fun formatSellZone(low: Double, high: Double): String {
    if (low <= 0 || high <= 0) return "--"
    return "${roundPrice(low)}–${roundPrice(high)}"
}

private fun findSwingHighs(bars: List<QuantBar>, left: Int, right: Int): List<Double> {
    if (bars.size < left + right + 1) return emptyList()
    val out = ArrayList<Double>()
    for (i in left until bars.size - right) {
        val h = bars[i].high
        var isPeak = true
        for (j in i - left until i) {
            if (bars[j].high >= h) {
                isPeak = false
                break
            }
        }
        if (!isPeak) continue
        for (j in i + 1..i + right) {
            if (bars[j].high > h) {
                isPeak = false
                break
            }
        }
        if (isPeak) out += h
    }
    return out
}

/**
 * 近端「高量节点」：在现价上方的价位桶中，取成交量最大桶的中位价。
 */
private fun highVolumeNode(bars: List<QuantBar>, price: Double): Double? {
    if (bars.size < 15 || price <= 0) return null
    val upside = bars.filter { it.high >= price * 0.99 && it.volume > 0 }
    if (upside.size < 5) return null
    val lo = upside.minOf { it.low }.coerceAtMost(price)
    val hi = upside.maxOf { it.high }
    if (hi <= lo) return null
    val buckets = 12
    val width = (hi - lo) / buckets
    if (width <= 0) return null
    val vol = DoubleArray(buckets)
    upside.forEach { bar ->
        val mid = (bar.high + bar.low) / 2.0
        val idx = ((mid - lo) / width).toInt().coerceIn(0, buckets - 1)
        vol[idx] += bar.volume
    }
    val best = vol.indices.maxByOrNull { vol[it] } ?: return null
    if (vol[best] <= 0) return null
    val node = lo + (best + 0.5) * width
    return node.takeIf { it >= price * 0.995 }
}

private fun sma(bars: List<QuantBar>, n: Int): Double {
    if (bars.size < n) return 0.0
    return bars.takeLast(n).map { it.close }.average()
}

private fun countResistanceRejects(bars: List<QuantBar>, resistance: Double): Int {
    if (resistance <= 0 || bars.size < 8) return 0
    val bandLow = resistance * 0.985
    var count = 0
    bars.takeLast(60).windowed(2).forEach { (a, b) ->
        val touched = a.high >= bandLow || b.high >= bandLow
        val faded = b.close < a.close && b.close < resistance * 0.99
        if (touched && faded) count++
    }
    return count.coerceAtMost(8)
}

private fun atr14(bars: List<QuantBar>): Double {
    if (bars.size < 2) return 0.0
    val trs = ArrayList<Double>(bars.size)
    for (i in 1 until bars.size) {
        val cur = bars[i]
        val prev = bars[i - 1]
        val tr = max(cur.high - cur.low, max(abs(cur.high - prev.close), abs(cur.low - prev.close)))
        trs += tr
    }
    return trs.takeLast(14).average()
}

private fun bollUpper20(bars: List<QuantBar>): Double {
    val closes = bars.map { it.close }.takeLast(20)
    if (closes.size < 10) return 0.0
    val ma = closes.average()
    val variance = closes.sumOf { (it - ma) * (it - ma) } / closes.size
    return ma + 2.0 * sqrt(variance)
}

private fun roundPrice(v: Double): Double =
    (v * 100.0).toInt() / 100.0

private fun round1(v: Double): String {
    val x = (v * 10.0).toInt() / 10.0
    return x.toString()
}
